import { useSelectValues } from 'hooks/useSelectValues'
import arrowRight from 'assets/img/arrowRight.webp'
import { getRequiredRule, getValidationRules } from 'utils/getValidationRules'
import { ContactInformationFormProps } from './types'
import countryOfResidence from 'constants/countryOfResidence'
import { Phone } from 'ui/molecules/Phone'
import { PHONE_NUMBER_LABELS, ITEM_NOT_FOUND } from 'texts/uiTexts'
import styles from './styles.module.scss'
import { ItemType } from 'ui/atoms/Select/types'

import { useMemo, useContext, useEffect, useRef } from 'react'
import {
  LOCATION_TABS,
  NEXT_BUTTON_TITLE,
  BUTTON_MAP_TITLE,
  LOCATION_ADDING_FORM_VALUES,
} from 'texts/locationDetails'
import { CONTACT_FORM_FIELDS } from '../../constants'
import { CONTACT_INFORMATION_FORM_VALUES } from 'texts/profileDetails'
import { EMPTY_RULES } from 'constants/form'
import { Rule } from 'ui/molecules/Form/components/FormItem/types'
import {
  formValuesSetting,
  LOCATION_TYPE_AIRPORT,
  LOCATION_TYPE_RAILWAY_STATION,
} from './constants'
import { ContextType } from 'ui/molecules/Form/types'
import { FormItemChildren, FormValuesSetting } from 'types/form'
import { PickupOptions } from 'api/locations/types'
import {
  OriginButton,
  Input,
  Textarea,
  Select,
  FormItem,
  FormContext,
} from '@frontend/design_system'
import { iataRegex, onlyNumbersWithOneDotAndMinusRegex } from 'constants/regex'
import { useDispatch, useSelector } from 'react-redux'
import { CrossHairIcon } from 'ui/icons'

import {
  setLocationType,
  setMapData,
  setMarkerPosition,
} from 'redux/locations/slice'
import { FormValues } from '../../types'
import {
  mapDataSelector,
  newLocationErrorSelector,
} from 'redux/locations/selectors'
import { useDidUpdate } from 'hooks/useDidUpdate'
import { scrollTop } from 'utils/scrollTop'
import { StateField } from 'ui/atoms/StateField'
import { Autocomplete } from 'ui/atoms/Autocomplete'
import { AddLocationModal } from './components/AddLocationModal'
import { USA_ID } from 'constants/locations'
import { joinClasses } from 'utils/joinClasses'
import { Item } from 'redux/locations/types'
import { Cor } from 'ui/molecules/Phone/types'
import { INVALID_LOCATION } from 'constants/errorCodes'
import { AutocompleteRef } from 'ui/atoms/Autocomplete/types'
import { UserRoles } from 'constants/roles'
import { useDynamicValidationRules } from 'modules/Locations/hooks/useDynamicValidationRules'

const requiredFields = {
  [LOCATION_ADDING_FORM_VALUES.COUNTRY.ID]:
    LOCATION_ADDING_FORM_VALUES.COUNTRY.REQUIRED_MESSAGE,
  [LOCATION_ADDING_FORM_VALUES.CITY.ID]:
    LOCATION_ADDING_FORM_VALUES.CITY.REQUIRED_MESSAGE,
  [LOCATION_ADDING_FORM_VALUES.LOCATION.ID]:
    LOCATION_ADDING_FORM_VALUES.LOCATION.REQUIRED_MESSAGE,
  [LOCATION_ADDING_FORM_VALUES.STREET.ID]:
    LOCATION_ADDING_FORM_VALUES.STREET.REQUIRED_MESSAGE,
  [LOCATION_ADDING_FORM_VALUES.BUILDING.ID]:
    LOCATION_ADDING_FORM_VALUES.BUILDING.REQUIRED_MESSAGE,
  [LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.ID]:
    LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.REQUIRED_MESSAGE,
}
const allMapFields = [
  ...Object.keys(requiredFields),
  LOCATION_ADDING_FORM_VALUES.STATE.ID,
]

const isMapReady = (values: Partial<FormValues>) =>
  Object.keys(requiredFields).every(
    (id) => (values as { [key: string]: string })[id]
  )

export const ContactInformationForm = ({
  currentLocation,
  values,
  changeActiveKey,
  locationTypes,
  locationData,
  isFormInitialized,
}: ContactInformationFormProps) => {
  const dispatch = useDispatch()
  const mapData = useSelector(mapDataSelector)
  const newLocationError = useSelector(newLocationErrorSelector)
  const { checkValidation, setErrors, setValues } =
    useContext<ContextType>(FormContext)
  const pickUpOptionsSelectValues = useSelectValues(
    Object.entries(PickupOptions).map(([key, value]) => ({
      id: key,
      name: value,
    }))
  )

  const { cityRules, locationRules } = useDynamicValidationRules(
    values,
    locationData.citiesSelectValues,
    locationData.locationsSelectValues
  )

  const cityAutocompleteRef = useRef<AutocompleteRef>(null)
  const locationAutocompleteRef = useRef<AutocompleteRef>(null)

  const {
    handleCountryChange,
    handleCityChange,
    handleLocationChange,
    handleStateChange,
    countriesSelectValues,
    citiesSelectValues,
    locationsSelectValues,
    statesSelectValues,
    countries,
    locations,
    loading,
  } = locationData

  const iataSelectValues: ItemType[] = useMemo(
    () =>
      locations.items
        .filter(
          (
            location
          ): location is typeof location & {
            meta: typeof location.meta & { shortCode: string }
          } =>
            location.meta.locationType === LOCATION_TYPE_AIRPORT &&
            Boolean(location.meta.shortCode)
        )
        .map((location) => ({
          label: location.meta.shortCode,
          value: location.meta.shortCode,
        })),
    [locations]
  )

  useDidUpdate(
    () => {
      if (mapData) {
        dispatch(setMapData(null))
      }
    },
    allMapFields.map((id) => (values as { [key: string]: string })[id])
  )

  const locationType = useMemo(
    () =>
      locations.items.find((el) => el.name === values.locationName)?.meta
        .locationType || '',
    [locations, values.locationName]
  )

  useEffect(() => {
    if (newLocationError) {
      if (newLocationError === INVALID_LOCATION) {
        locationAutocompleteRef.current?.open()
      } else {
        cityAutocompleteRef.current?.open()
      }
    }
  }, [newLocationError])

  useEffect(() => {
    dispatch(setLocationType(locationType))
  }, [locationType])

  useEffect(() => {
    if (locationType)
      setValues((prev) => ({
        ...prev,
        [LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID]:
          locationTypes.find((type) => {
            if (type.name === LOCATION_TYPE_RAILWAY_STATION) {
              return type.name.replace(' ', '') === locationType
            } else {
              return type.name === locationType
            }
          })?.id || values.newLocationType,
      }))
    setErrors((prev) => ({
      ...prev,
      [LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID]: '',
    }))
  }, [locationTypes, locationType, isFormInitialized])

  const goNextTab = () => {
    const { newErrors } = checkValidation()
    const filteredErrors = Object.keys(CONTACT_FORM_FIELDS).reduce(
      (acc: Record<string, unknown>, item) => {
        if (newErrors[item]) {
          acc[item] = newErrors[item]
        }
        return acc
      },
      {}
    )

    if (!Object.keys(filteredErrors).length) {
      scrollTop()
      changeActiveKey(LOCATION_TABS.WORKING_HOURS)
      setErrors(() => ({
        [LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.ID]: '',
      }))
    } else {
      setErrors(() => ({
        ...filteredErrors,
        [LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.ID]: '',
      }))
    }
  }

  const phoneCorRules: Rule[] = useMemo(
    () => [
      {
        type: 'required',
        message: CONTACT_INFORMATION_FORM_VALUES.PHONE_COR.REQUIRED_MESSAGE,
      },
    ],
    []
  )

  const showMap = () => {
    dispatch(setMarkerPosition({ lat: 0, lng: 0 }))
    dispatch(
      setMapData({
        country: countriesSelectValues.find(
          (item) => item.value === values.countryId
        )?.label,
        city:
          citiesSelectValues.find((item) => item.value === values.cityId)
            ?.label || values.cityId,
        location:
          locationsSelectValues.find((item) => item.value === values.locationId)
            ?.label || values.locationId,
        street: values.street,
        region: values.region,
        building: values.building,
        postalCode: values.postalCode,
      })
    )
  }

  const validationRules = (field: FormValuesSetting['key']) =>
    useMemo(() => getValidationRules(field, UserRoles.Operator), [])

  const getCor = (country: ItemType) => {
    const countryCode = countries.items.find((el) => el.name === country.label)
      ?.meta?.CountryAlpha2Code
    const selectedCountry = countryOfResidence.find(
      (el) => el.code === countryCode
    )
    return selectedCountry
  }

  const isStateWithDropdown = Number(currentLocation.country?.id) === USA_ID

  const isCityDisabled = useMemo(
    () =>
      !currentLocation.country.name ||
      (isStateWithDropdown && !currentLocation.state.id),
    [currentLocation]
  )
  const stateValidationRule = useMemo(
    () =>
      isStateWithDropdown
        ? getRequiredRule(
            LOCATION_ADDING_FORM_VALUES.STATE.SELECT_REQUIRED_MESSAGE
          )
        : formValuesSetting.state.validationRules,
    [isStateWithDropdown]
  )

  const onChangeCountry = (
    item: Item,
    onChangeField: (value: string | Cor | undefined, field: string) => void
  ) => {
    handleCountryChange(item)
    onChangeField(getCor(item), LOCATION_ADDING_FORM_VALUES.PHONE_COR.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.STATE.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.LOCATION.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.CITY.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.STREET.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.BUILDING.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.LATITUDE.ID)
    onChangeField('', LOCATION_ADDING_FORM_VALUES.LONGITUDE.ID)
  }

  const isExistedIata = useMemo(
    () =>
      !!locations.items.find(
        (location) =>
          values.shortCode && location.meta.shortCode === values.shortCode
      ),
    [locations, values.shortCode]
  )

  const isDuplicateLocationType = useMemo(
    () => locations.items.some((location) => location.meta.isDuplicate),
    [locations]
  )

  const iataValidationRule = useMemo(
    () =>
      values.newLocationType === LOCATION_TYPE_AIRPORT
        ? [
            {
              type: 'required',
              message: LOCATION_ADDING_FORM_VALUES.IATA.REQUIRED_MESSAGE,
            },
            {
              type: 'pattern',
              value: iataRegex,
              message: LOCATION_ADDING_FORM_VALUES.IATA.VALIDATION_MESSAGE,
            },
          ]
        : [],
    [values.newLocationType]
  )

  const isAirport =
    locationType === LOCATION_TYPE_AIRPORT ||
    values.newLocationType === LOCATION_TYPE_AIRPORT

  useEffect(() => {
    if (isDuplicateLocationType && !currentLocation.location.id) {
      setValues((prev) => ({
        ...prev,
        [LOCATION_ADDING_FORM_VALUES.LOCATION.ID]: locations.items[0].name,
      }))
    }
  }, [isDuplicateLocationType])

  return (
    <>
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.COUNTRY.ID}
          className={joinClasses(styles['form-item-country'], [
            styles['with-state'],
            isStateWithDropdown,
          ])}
          rules={validationRules(formValuesSetting.countryId)}
        >
          {({ value, error, onChange, onChangeField }: FormItemChildren) => (
            <Select
              size="large"
              placeholder={LOCATION_ADDING_FORM_VALUES.COUNTRY.PLACEHOLDER}
              onChange={(el: string, item: ItemType) => {
                onChange(el)
                onChangeCountry(item, onChangeField)
              }}
              className={styles.select}
              label={LOCATION_ADDING_FORM_VALUES.COUNTRY.LABEL}
              items={countriesSelectValues}
              error={error}
              selectedValue={value}
              isRequired
              search={{
                placeholder:
                  LOCATION_ADDING_FORM_VALUES.COUNTRY.SEARCH_PLACEHOLDER,
              }}
              loading={loading.countries}
            />
          )}
        </FormItem>
        {isStateWithDropdown && (
          <FormItem
            id={LOCATION_ADDING_FORM_VALUES.STATE.ID}
            className={styles['form-item']}
            rules={stateValidationRule}
          >
            {({ value, error, onChange, onChangeField }: FormItemChildren) => (
              <StateField
                value={value}
                error={error}
                handleStateChange={(item) => {
                  handleStateChange(item)
                  onChangeField('', LOCATION_ADDING_FORM_VALUES.LOCATION.ID)
                  onChangeField('', LOCATION_ADDING_FORM_VALUES.CITY.ID)
                  onChangeField(
                    '',
                    LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID
                  )
                }}
                isStateWithDropdown
                onChange={onChange}
                states={statesSelectValues}
              />
            )}
          </FormItem>
        )}
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.CITY.ID}
          className={styles['form-item']}
          rules={cityRules}
        >
          {({ value, error, onChange, onChangeField }: FormItemChildren) => (
            <Autocomplete
              ref={cityAutocompleteRef}
              size="large"
              placeholder={LOCATION_ADDING_FORM_VALUES.CITY.PLACEHOLDER}
              onChange={(el: string, item: ItemType) => {
                onChange(el)
                handleCityChange(item)
                onChangeField('', LOCATION_ADDING_FORM_VALUES.LOCATION.ID)
                onChangeField(
                  '',
                  LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID
                )
                onChangeField('', LOCATION_ADDING_FORM_VALUES.IATA.ID)
                dispatch(setLocationType(''))
              }}
              className={styles.select}
              label={LOCATION_ADDING_FORM_VALUES.CITY.LABEL}
              items={citiesSelectValues}
              error={error}
              noSpaceForError
              selectedValue={value}
              isRequired
              disabled={isCityDisabled}
              notFoundTitle={ITEM_NOT_FOUND}
            />
          )}
        </FormItem>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.locationType)}
        >
          {({ value, error, onChange, onChangeField }: FormItemChildren) => (
            <Select
              selectedValue={value}
              placeholder={
                LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.PLACEHOLDER
              }
              items={
                locationTypes?.map((el) => ({
                  label: el.name,
                  value: el.id,
                })) || []
              }
              size="large"
              disabled={!!locationType || !values.cityName}
              error={error}
              onChange={(el: string) => {
                onChange(el)

                const currentLocationName = `${values.cityName} ${
                  locationTypes.find((type) => type.id === el)?.name || ''
                }`
                const iataCode = locations.items.find(
                  (location) => location.name === currentLocationName
                )?.meta.shortCode
                onChangeField(
                  iataCode || '',
                  LOCATION_ADDING_FORM_VALUES.IATA.ID
                )
                onChangeField(
                  currentLocationName,
                  LOCATION_ADDING_FORM_VALUES.LOCATION.ID
                )
                dispatch(setLocationType(el))
              }}
              label={LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.LABEL}
              isRequired
            />
          )}
        </FormItem>
      </div>
      {isAirport && !isDuplicateLocationType && (
        <div className={styles['form-items-row']}>
          <FormItem
            id={LOCATION_ADDING_FORM_VALUES.IATA.ID}
            className={styles.iata}
            rules={iataValidationRule}
          >
            {({ value, error, onChange, onChangeField }: FormItemChildren) => (
              <Autocomplete
                size="large"
                placeholder={LOCATION_ADDING_FORM_VALUES.IATA.PLACEHOLDER}
                onChange={(shortCode: string) => {
                  onChange(shortCode)
                  const locationWithCurrentShortCode = locations.items.find(
                    (location) => location.meta.shortCode === shortCode
                  )
                  if (shortCode && locationWithCurrentShortCode)
                    onChangeField(
                      locationWithCurrentShortCode.name,
                      LOCATION_ADDING_FORM_VALUES.LOCATION.ID
                    )
                }}
                label={LOCATION_ADDING_FORM_VALUES.IATA.LABEL}
                items={iataSelectValues}
                error={error}
                maxLength={3}
                tooltip={LOCATION_ADDING_FORM_VALUES.IATA.TOOLTIP}
                selectedValue={value}
                isRequired
                notFoundTitle={ITEM_NOT_FOUND}
              />
            )}
          </FormItem>
        </div>
      )}
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.LOCATION.ID}
          className={styles['form-item']}
          rules={locationRules}
        >
          {({ value, error, onChange, onChangeField }: FormItemChildren) => (
            <Autocomplete
              ref={locationAutocompleteRef}
              size="large"
              placeholder={LOCATION_ADDING_FORM_VALUES.LOCATION.PLACEHOLDER}
              onChange={(el: string, item: ItemType) => {
                onChange(el)
                handleLocationChange(item)
                onChangeField(
                  'Default',
                  LOCATION_ADDING_FORM_VALUES.PICKUP_OPTION.ID
                )
                const currentLocationShortCode = locations.items.find(
                  (location) => location.name === el
                )?.meta.shortCode
                onChangeField(
                  currentLocationShortCode || '',
                  LOCATION_ADDING_FORM_VALUES.IATA.ID
                )

                setErrors((prev) => ({
                  ...prev,
                  [LOCATION_ADDING_FORM_VALUES.IATA.ID]: '',
                }))
              }}
              className={styles.select}
              label={LOCATION_ADDING_FORM_VALUES.LOCATION.LABEL}
              items={locationsSelectValues}
              error={error}
              noSpaceForError
              selectedValue={value}
              tooltip={
                isExistedIata
                  ? LOCATION_ADDING_FORM_VALUES.LOCATION.IATA_TOOLTIP
                  : LOCATION_ADDING_FORM_VALUES.LOCATION.TOOLTIP
              }
              disabled={
                !(currentLocation.city.name || value) ||
                (isExistedIata && !isDuplicateLocationType && isAirport) ||
                !values.newLocationType
              }
              isRequired
              notFoundTitle={ITEM_NOT_FOUND}
            />
          )}
        </FormItem>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.STREET.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.street)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={LOCATION_ADDING_FORM_VALUES.STREET.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="none"
              onChange={onChange}
              label={LOCATION_ADDING_FORM_VALUES.STREET.LABEL}
              isRequired
            />
          )}
        </FormItem>
      </div>
      <AddLocationModal
        currentLocation={currentLocation}
        states={statesSelectValues}
        handleStateChange={handleStateChange}
        handleCityChange={handleCityChange}
        cities={citiesSelectValues}
      />
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.BUILDING.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.building)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={LOCATION_ADDING_FORM_VALUES.BUILDING.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              onChange={onChange}
              label={LOCATION_ADDING_FORM_VALUES.BUILDING.LABEL}
              isRequired
            />
          )}
        </FormItem>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.postalCode)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              onChange={onChange}
              label={LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.LABEL}
              isRequired
            />
          )}
        </FormItem>
      </div>
      <div className={styles['form-item-right']}>
        <div className={styles['form-item-map-button']}>
          <OriginButton
            onClick={showMap}
            size="large"
            iconLeft={
              <CrossHairIcon
                size="medium"
                color={
                  !isMapReady(values as FormValues)
                    ? 'blueSecondary'
                    : 'whiteMaster'
                }
              />
            }
            label={BUTTON_MAP_TITLE}
            disabled={!isMapReady(values as FormValues)}
          />
        </div>
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.LATITUDE.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.latitude)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={LOCATION_ADDING_FORM_VALUES.LATITUDE.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              onChange={(value: string) => {
                const lat = value.replace(
                  onlyNumbersWithOneDotAndMinusRegex,
                  ''
                )
                dispatch(setMarkerPosition({ lat: +lat }))
                onChange(lat)
              }}
              label={LOCATION_ADDING_FORM_VALUES.LATITUDE.LABEL}
              isRequired
            />
          )}
        </FormItem>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.LONGITUDE.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.longitude)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={LOCATION_ADDING_FORM_VALUES.LONGITUDE.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              onChange={(value: string) => {
                const lng = value.replace(
                  onlyNumbersWithOneDotAndMinusRegex,
                  ''
                )
                dispatch(setMarkerPosition({ lng: +lng }))
                onChange(lng)
              }}
              label={LOCATION_ADDING_FORM_VALUES.LONGITUDE.LABEL}
              isRequired
            />
          )}
        </FormItem>
      </div>
      <div className={styles['form-items-full']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.PHONE_NUMBER.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.phone)}
        >
          {({
            value: number,
            onChange: changeNumber,
            error,
          }: FormItemChildren) => (
            <FormItem
              id={LOCATION_ADDING_FORM_VALUES.PHONE_COR.ID}
              className={styles.phone}
              rules={number?.length ? phoneCorRules : EMPTY_RULES}
            >
              {({
                value: cor,
                onChange,
                error: corError,
              }: FormItemChildren) => (
                <Phone
                  label={LOCATION_ADDING_FORM_VALUES.PHONE_NUMBER.LABEL}
                  number={number}
                  changeNumber={changeNumber}
                  cor={cor}
                  changeCOR={onChange}
                  corList={countryOfResidence}
                  locales={{
                    ofLabel: PHONE_NUMBER_LABELS.OF,
                    searchPlaceholder: PHONE_NUMBER_LABELS.SEARCH,
                    save: PHONE_NUMBER_LABELS.SAVE,
                  }}
                  isRequired
                  spaceForError="auto"
                  error={error || corError}
                  placeholder={
                    LOCATION_ADDING_FORM_VALUES.PHONE_NUMBER.PLACEHOLDER
                  }
                />
              )}
            </FormItem>
          )}
        </FormItem>
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.EMAIL.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.email)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={LOCATION_ADDING_FORM_VALUES.EMAIL.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              tooltip={LOCATION_ADDING_FORM_VALUES.EMAIL.TOOLTIP}
              onChange={onChange}
              label={LOCATION_ADDING_FORM_VALUES.EMAIL.LABEL}
              isRequired
            />
          )}
        </FormItem>
        {isAirport && (
          <FormItem
            id={LOCATION_ADDING_FORM_VALUES.PICKUP_OPTION.ID}
            className={styles['form-item']}
          >
            {({ value, error, onChange }: FormItemChildren) => (
              <Select
                size="large"
                placeholder={
                  LOCATION_ADDING_FORM_VALUES.PICKUP_OPTION.PLACEHOLDER
                }
                error={error}
                onChange={onChange}
                label={LOCATION_ADDING_FORM_VALUES.PICKUP_OPTION.LABEL}
                items={pickUpOptionsSelectValues}
                selectedValue={value}
              />
            )}
          </FormItem>
        )}
      </div>
      <FormItem
        id={LOCATION_ADDING_FORM_VALUES.PICKUP_INSTRUCTIONS.ID}
        rules={validationRules(formValuesSetting.pickUpInstruction)}
        className={styles['text-area-field']}
      >
        {({ value, error, onChange }: FormItemChildren) => (
          <Textarea
            value={value}
            onChange={onChange}
            tooltip={LOCATION_ADDING_FORM_VALUES.PICKUP_INSTRUCTIONS.TOOLTIP}
            label={LOCATION_ADDING_FORM_VALUES.PICKUP_INSTRUCTIONS.LABEL}
            placeholder={
              LOCATION_ADDING_FORM_VALUES.PICKUP_INSTRUCTIONS.PLACEHOLDER
            }
            error={error}
            placeholderStyle="italic"
          />
        )}
      </FormItem>
      <FormItem
        id={LOCATION_ADDING_FORM_VALUES.DROPOFF_INSTRUCTIONS.ID}
        rules={validationRules(formValuesSetting.dropOffInstruction)}
        className={styles['text-area-field']}
      >
        {({ value, error, onChange }: FormItemChildren) => (
          <Textarea
            value={value}
            onChange={onChange}
            tooltip={LOCATION_ADDING_FORM_VALUES.DROPOFF_INSTRUCTIONS.TOOLTIP}
            label={LOCATION_ADDING_FORM_VALUES.DROPOFF_INSTRUCTIONS.LABEL}
            placeholder={
              LOCATION_ADDING_FORM_VALUES.DROPOFF_INSTRUCTIONS.PLACEHOLDER
            }
            placeholderStyle="italic"
            error={error}
            height={110}
          />
        )}
      </FormItem>
      <div className={styles['form-item-right']}>
        <div className={styles['form-next-button']}>
          <OriginButton
            variant="link"
            size="large"
            onClick={goNextTab}
            label={NEXT_BUTTON_TITLE}
            typographyName="Button1"
            iconRight={<img src={arrowRight} width={16} height={16} />}
          />
        </div>
      </div>
    </>
  )
}
